import { CurationItem } from "../../types";
import { callCurationProxy } from "../backend/backendClient";
import { DEFAULT_SMART_LIST_SORT_OPTION } from "../../constants";
import { ensureTrimmed, parseJsonOrThrow, readTextOrThrow } from "./helpers";

/**
 * Options for updating a list item
 * @property alias - Alias of the list item
 * @property pageSlug - Slug of the page associated with the list item
 * @property layoutMenuPosition - Position of the list item in the menu
 * @property position - Position of the list item in the menu
 * @property smartListTemplate - Smart list template ID
 * @property smartListAdapterId - Smart list adapter ID (recommendation engine)
 * @property smartListGenre - Smart list genre (for genre-based templates)
 * @property smartListLabels - Smart list labels (for label-based templates)
 * @property smartListSortOption - Smart list sort option
 */
export interface UpdateListOptions {
    alias?: string;
    pageSlug?: string;
    layoutMenuPosition?: "Header" | "Footer";
    position?: string;
    smartListTemplate?: string;
    smartListAdapterId?: number;
    smartListGenre?: string;
    smartListLabels?: string[];
    smartListSortOption?: string;
}

/**
 * Update a list item
 * @param item
 * @param title
 * @param options
 */
export const updateListItem = async (
    item: CurationItem,
    title: string,
    options: UpdateListOptions = {},
): Promise<CurationItem> => {
    if (!item?.id) {
        throw new Error("updateListItem requires an item with an ID.");
    }

    const normalizedTitle = ensureTrimmed(title);
    if (!normalizedTitle) {
        throw new Error("updateListItem requires a non-empty title.");
    }

    const metadata = item.metadata ?? {};
    const defaultMetadata = { ...(metadata?.default ?? {}) };
    const enMetadata = { ...(metadata?.en_US ?? {}) };

    if (options.alias) {
        defaultMetadata.alias = options.alias;
    }
    if (options.pageSlug) {
        defaultMetadata["page-slug"] = options.pageSlug;
    }
    if (options.layoutMenuPosition) {
        defaultMetadata["layout-menu-position"] = options.layoutMenuPosition;
    }
    if (options.position) {
        enMetadata.url = options.position;
    }

    // Handle smart list fields
    let templateId = item.templateId;
    let adapterId = item.adapterId;
    let smartList = item.smartList;

    if (options.smartListTemplate !== undefined) {
        const templateIdNum = Number(options.smartListTemplate);
        templateId = Number.isFinite(templateIdNum) ? templateIdNum : item.templateId;
    }

    if (options.smartListAdapterId !== undefined) {
        adapterId = options.smartListAdapterId;
    }

    const smartListFieldsWereProvided =
        options.smartListGenre !== undefined ||
        options.smartListLabels !== undefined ||
        options.smartListSortOption !== undefined;

    if (smartListFieldsWereProvided) {
        let labelsString: string;
        if (options.smartListLabels !== undefined) {
            const normalizedLabels =
                options.smartListLabels
                    ?.map((label) => label.toLowerCase().trim())
                    .filter((label) => label.length > 0) ?? [];
            labelsString = normalizedLabels.length > 0 ? normalizedLabels.join(",") : "";
        } else if (options.smartListGenre !== undefined) {
            labelsString = options.smartListGenre?.toLowerCase().trim() ?? "";
        } else {
            labelsString = item.smartList?.values?.labels ?? "";
        }

        const sortOption =
            options.smartListSortOption ??
            item.smartList?.values?.sort ??
            DEFAULT_SMART_LIST_SORT_OPTION;

        // Update smartList object
        const existingSmartList = item.smartList ?? ({} as Record<string, unknown>);
        const existingValues =
            (existingSmartList as { values?: Record<string, unknown> })?.values ?? {};
        const existingRequestMap =
            (existingSmartList as { requestMap?: Record<string, unknown> })?.requestMap ?? {};
        const existingOptions =
            (existingRequestMap as { options?: Record<string, unknown> })?.options ?? {};
        const existingQuery = (existingOptions as { query?: Record<string, unknown> })?.query ?? {};

        const normalizedQueryValue = labelsString.length > 0 ? `labels:${labelsString}` : undefined;

        const queryParameters: Record<string, unknown> = {
            ...existingQuery,
            page: {
                size: "50",
            },
            sort: sortOption,
            platform: "web",
        };

        if (normalizedQueryValue) {
            queryParameters.query = normalizedQueryValue;
        } else if ("query" in queryParameters) {
            delete queryParameters.query;
        }

        smartList = {
            ...existingSmartList,
            values: {
                ...existingValues,
                sort: sortOption,
                labels: labelsString,
            },
            requestMap: {
                ...existingRequestMap,
                url: "https://sales.content-discovery.cf.eu-north-1-prod.vmnd.tv/api/v1/assets",
                method: "get",
                options: {
                    ...existingOptions,
                    json: true,
                    query: {
                        ...queryParameters,
                    } as Record<string, unknown>,
                    headers: {
                        Accept: "application/json",
                        "Accept-Language": "en-us",
                        "Content-Type": "application/json",
                    },
                    timeout: 2000,
                },
                firstSortIndex: 0,
            },
        } as unknown as typeof item.smartList;
    }

    const updateBody = {
        ...item,
        title: normalizedTitle,
        ...(templateId !== undefined && { templateId }),
        ...(adapterId !== undefined && { adapterId }),
        ...(smartList !== undefined && { smartList }),
        metadata: {
            ...metadata,
            default: defaultMetadata,
            en_US: { ...enMetadata, title: normalizedTitle },
        },
    };

    const updateResponseText = await readTextOrThrow(
        await callCurationProxy({
            path: `/list/${item.id}`,
            method: "PUT",
            body: updateBody,
        }),
        "List item update (PUT)",
    );

    return parseJsonOrThrow<CurationItem>(updateResponseText, "List item update");
};
